import type { Task } from '@domain/state.ts'
import { es } from '../strings/es.ts'

/**
 * La barra de lo que ya tocaba y sigue sin hacer.
 *
 * Es lo que de verdad mueve la aguja, más que una notificación: una
 * notificación es un instante que se ve o no se ve, y esto es un estado. Quien
 * coja la tablet a las nueve y media lo lee sin tener que acordarse de nada.
 *
 * Tres decisiones que la hacen usable y no un fastidio:
 *
 * **No se puede cerrar, pero se va sola.** Cerrarla sería dejar de saberlo; que
 * desaparezca al terminar la rutina es lo que la convierte en un indicador de
 * progreso en vez de una regañina. Es la única forma de quitarla, y es la buena.
 *
 * **No estorba.** Una barra fina arriba, con la app entera usable debajo. No es
 * un modal ni bloquea nada: el niño puede ir a la tienda, mirar el temporizador
 * o no hacer nada, y sigue viéndola.
 *
 * **Dice el hecho, no una orden.** «Rutina de la noche, sin hacer» y no «¡no
 * olvides tu rutina!». Para el niño que lee literal, un hecho es accionable y
 * una exhortación es ruido — y además la app no le habla así en ningún otro
 * sitio.
 */
export function Pendiente({
  tasks,
  accent,
  onOpen,
}: {
  tasks: readonly Task[]
  accent: string
  onOpen: (task: Task) => void
}) {
  const primera = tasks[0]
  if (!primera) return null

  return (
    <button
      type="button"
      onClick={() => {
        onOpen(primera)
      }}
      className="bg-gold-soft border-gold flex w-full items-center gap-3 border-b-2 px-4 py-3 text-left"
      style={{ borderColor: accent }}
    >
      <span aria-hidden="true" className="text-2xl">
        {primera.emoji ?? '⭐'}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-extrabold">{es.pendiente.title(primera.name)}</span>
        <span className="text-muted block text-sm">
          {tasks.length > 1 ? es.pendiente.andMore(tasks.length - 1) : es.pendiente.tap}
        </span>
      </span>
    </button>
  )
}

/**
 * La misma idea en la pantalla de «quién eres», donde todavía no se sabe quién
 * tiene la tablet. Nombra a quién le toca, porque el que la coge puede ser el
 * otro — y así el que la coge se lo dice al que le toca, que es exactamente lo
 * que se quería: que el aviso no sea un padre.
 */
export function PendienteDeOtro({
  nombres,
  onPick,
}: {
  nombres: readonly { readonly id: string; readonly name: string; readonly accent: string }[]
  onPick: (id: string) => void
}) {
  if (nombres.length === 0) return null

  return (
    <div className="bg-gold-soft border-gold mb-6 rounded-xl border-t-4 p-4">
      <p className="m-0 mb-2 text-sm font-extrabold">{es.pendiente.whose}</p>
      <div className="flex flex-wrap gap-2">
        {nombres.map((quien) => (
          <button
            key={quien.id}
            type="button"
            onClick={() => {
              onPick(quien.id)
            }}
            className="rounded-lg border-2 px-4 py-2 font-bold"
            style={{ borderColor: quien.accent, color: quien.accent }}
          >
            {quien.name}
          </button>
        ))}
      </div>
    </div>
  )
}
